import type { CollapseProps } from 'antd/es/collapse';
import Collapse from 'antd/es/collapse';
import { default as Modal } from 'antd/es/modal';
import { default as Skeleton } from 'antd/es/skeleton';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import {
  useGetCustomerConfigurations,
  useGetDisbursementPayoutBatchesBatchId,
  usePostDisbursementPayoutBatchesBatchIdApprove,
  usePostDisbursementPayoutBatchesBatchIdReject,
  type PayoutBatches
} from 'api';

import { selectUserId } from 'reduxLocal/slices/authSlice';

import type { ApprovalAction, Reviewer } from 'models/ApprovalWorkFlow';

import { useOrganisationId } from 'hooks/context';
import { useFeatureAccessControl } from 'hooks/useFeatureAccessControl';
import { useGoogleAnalyticsDispatcher } from 'hooks/useGoogleAnalyticsDispatcher';
import { useOrgNavigate } from 'hooks/useOrgNavigate';
import { usePermissions } from 'hooks/usePermissions';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import { BatchApprovalSection } from './BatchApprovalSection';
import { getCollapseItems } from './collapseItems';
import { TRANSLATIONS } from './constants';

import { BATCHES_DETAILS_TEST_ID } from '../constants';

export type BATCH_SECTIONS = 'batch' | 'attachments';

type CollapseItemType = NonNullable<CollapseProps['items']>[number];

export interface CollapseItemWithTypedSection
  extends Omit<CollapseItemType, 'key'> {
  key?: BATCH_SECTIONS;
}

type Props = {
  onClose: VoidFunction;
  selectedRowData: PayoutBatches;
  onApproveOrReject: (action: ApprovalAction) => void;
};

export const BatchDetails = ({
  onClose,
  selectedRowData,
  onApproveOrReject
}: Props) => {
  const { isBatchApprovalRequired } = useFeatureAccessControl();
  const { isPermitted } = usePermissions();

  const openNotification = useOpenNotification();
  const [isDeclineDialogOpen, setIsDeclineDialogOpen] = useState(false);
  const userId = useSelector(selectUserId);
  if (!userId) throw new Error('Missing user id');
  const organisationId = useOrganisationId<number>();
  const batchId = selectedRowData.id;

  const navigate = useOrgNavigate();

  const {
    data: { data: batchDetails } = {},
    isLoading,
    isError,
    isSuccess
  } = useGetDisbursementPayoutBatchesBatchId(organisationId, batchId);

  const { data: { data: orgSettings } = {} } =
    useGetCustomerConfigurations(organisationId);

  const dispatchGAEvent = useGoogleAnalyticsDispatcher();

  const isApprovedByUser = useMemo(() => {
    return batchDetails?.approvals?.some(
      (approval: Reviewer) => approval.user_id === userId
    );
  }, [batchDetails?.approvals, userId]);

  const requiredNumberOfApprovals = Number(
    orgSettings!.batch_number_of_approvers
  );

  const remainingApprovals =
    requiredNumberOfApprovals - (batchDetails?.approvals?.length ?? 0);

  const { mutate: approveBatch, isPending: isApprovalPending } =
    usePostDisbursementPayoutBatchesBatchIdApprove();

  const { mutate: rejectBatch, isPending: isRejectionPending } =
    usePostDisbursementPayoutBatchesBatchIdReject();

  const handleApprove = useCallback(() => {
    if (isApprovalPending) {
      return;
    }
    dispatchGAEvent('batch_approval', {
      approval: 'approve'
    });
    approveBatch(
      {
        organisationId,
        batchId
      },
      {
        onSuccess: () => {
          onApproveOrReject('approve');
          openNotification({
            kind: 'success',
            message: TRANSLATIONS.APPROVE_BATCH_SUCCESS(batchId)
          });

          if (remainingApprovals === 1) {
            // Redirect to approved page if the user is the last one to approve
            navigate('/batches?status=completed');
          } else {
            // Redirect to pending approval if other approvals are still required
            navigate(`/batches/${batchDetails?.id}?status=waiting_review`);
          }
        },
        onError: () => {
          openNotification({
            kind: 'error',
            message: TRANSLATIONS.APPROVE_BATCH_ERROR
          });
        },
        onSettled: () => {
          onClose();
        }
      }
    );
  }, [
    isApprovalPending,
    dispatchGAEvent,
    approveBatch,
    organisationId,
    batchId,
    onApproveOrReject,
    openNotification,
    remainingApprovals,
    navigate,
    batchDetails?.id,
    onClose
  ]);

  const handleReject = useCallback(() => {
    if (isRejectionPending) {
      return;
    }
    dispatchGAEvent('batch_approval', {
      approval: 'reject'
    });
    rejectBatch(
      {
        organisationId,
        batchId
      },
      {
        onSuccess: () => {
          onApproveOrReject('reject');
          openNotification({
            kind: 'success',
            message: TRANSLATIONS.REJECT_BATCH_SUCCESS(batchId)
          });
          navigate('/batches?status=rejected');
        },
        onError: () => {
          openNotification({
            kind: 'error',
            message: TRANSLATIONS.REJECT_BATCH_ERROR
          });
        },
        onSettled: () => {
          onClose();
        }
      }
    );
  }, [
    isRejectionPending,
    dispatchGAEvent,
    rejectBatch,
    organisationId,
    batchId,
    onApproveOrReject,
    openNotification,
    navigate,
    onClose
  ]);

  const canApproveBase =
    isBatchApprovalRequired &&
    !isApprovedByUser &&
    batchDetails !== undefined &&
    batchDetails.status === 'waiting_review' &&
    !batchDetails.rejections?.length &&
    batchDetails.creator_id !== userId;

  const canApprove = canApproveBase && isPermitted('payout_batches:review');

  const collapseItems: CollapseItemWithTypedSection[] = useMemo(() => {
    return getCollapseItems(batchDetails, isSuccess);
  }, [isSuccess, batchDetails]);

  return (
    <div
      style={{ flex: 0.25, borderRadius: '.75rem' }}
      data-testid={BATCHES_DETAILS_TEST_ID}
      className="cool-shadow overflow-y-auto border"
    >
      <div className="flex justify-between rounded-t-xl px-6 py-3.5 text-xl">
        <span>Details</span>
      </div>
      <div className="overflow-auto">
        <div className="flex flex-col gap-3">
          {isError && (
            <div className="flex h-full items-center justify-center">
              Something went wrong
            </div>
          )}

          {isLoading && <Skeleton className="p-6" />}

          {isSuccess && (
            <div>
              <BatchApprovalSection
                batchDetails={batchDetails}
                userId={userId}
                remainingApprovals={remainingApprovals}
                isBatchApprovalRequired={isBatchApprovalRequired}
                canApprove={canApprove}
                onOpenDeclineDialog={() => setIsDeclineDialogOpen(true)}
                onApprove={handleApprove}
              />

              <Collapse
                className="antd-details-collapse bg-white"
                expandIconPosition="end"
                accordion
                bordered={false}
                defaultActiveKey="batch"
                items={collapseItems}
              />

              <Modal
                open={isDeclineDialogOpen}
                onCancel={() => setIsDeclineDialogOpen(false)}
                footer={
                  <div className="grid w-full grid-cols-2 gap-4">
                    <SecondaryButton
                      onClick={() => setIsDeclineDialogOpen(false)}
                    >
                      Cancel
                    </SecondaryButton>
                    <PrimaryButton danger onClick={handleReject}>
                      Decline
                    </PrimaryButton>
                  </div>
                }
              >
                <div className="text-lg font-bold">Decline batch?</div>
                <div className="mb-4">
                  This batch will be declined and the enclosed payouts will not
                  be processed.
                </div>
              </Modal>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
